/**
 * Consumer Wallet — Trust Wallet pathway.
 *
 * Renders at /j/:token. Ported from the uploaded Expo React Native app
 * (altara-trust-lens) to React web, preserving the wallet aesthetic:
 *   - Deep navy hero (#0A1F44) with gold accent (#C9A24A)
 *   - Rounded-2xl elevated cards
 *   - Institution logo letter + advisor + "sent X ago"
 *   - Status selector (reviewing / need_more_info / interested / not_interested)
 *   - NAVI plain-language summary (Claude-generated)
 *   - Documents vault
 *   - Quick-check assessment (unlocks when "interested")
 *   - Decision confidence + confirmation
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, FileText, Sparkles, CheckCircle2, Circle,
  ExternalLink, MessageCircleQuestion, ChevronRight, Send,
  Loader2, AlertTriangle, HelpCircle, Lock, Wallet, Upload, X,
} from "lucide-react";
import { loadWallet, updateWallet, registerJourneyToken } from "@/lib/wallet-storage";
import { WalletShell } from "@/components/wallet/WalletShell";
import { emitSignal } from "@/lib/wallet-signal";
import { useAccessibility } from "@/contexts/AccessibilityContext";
import FeedbackRow from "@/components/FeedbackRow";
import VideoEmbed, { formatDuration } from "@/components/VideoEmbed";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Wallet palette (from uploaded Trust Wallet lib/theme.ts)
const NAVY = "#0A1F44";
const NAVY_DARK = "#061633";
const GOLD = "#C9A24A";
const BG = "#F8F9FA";

const DOC_KIND_LABEL = {
  quote: "Quote", disclosure: "TCF Disclosure", brochure: "Product Brochure",
  policy_wording: "Policy Wording", fsca_disclosure: "FSCA Disclosure",
  statement_of_advice: "Statement of Advice", policy_schedule: "Policy Schedule",
  application_form: "Application Form", terms_and_conditions: "Terms & Conditions",
  kyc: "KYC / FICA", other: "Document",
};

const STATUS_LABEL = {
  reviewing: "Reviewing",
  need_more_info: "Need more info",
  interested: "Interested",
  not_interested: "Not interested",
  decision_recorded: "Decision recorded",
};

const STATUS_OPTIONS = ["reviewing", "need_more_info", "interested", "not_interested"];

const QUICK_CHECKS = [
  { id: "premium",    q: "Do you understand how the monthly premium is calculated?" },
  { id: "cancel",     q: "Do you understand what happens if you cancel this product?" },
  { id: "claim",      q: "Do you understand how a claim would be paid?" },
  { id: "exclusions", q: "Do you know what is NOT covered (exclusions)?" },
];
const CHECK_OPTIONS = [
  { value: "yes",         label: "Yes, clearly",  tone: "emerald" },
  { value: "roughly",     label: "Roughly",       tone: "amber" },
  { value: "not_really",  label: "Not really",    tone: "rose" },
];

const DECISION_CHOICES = [
  { value: "very_confident",       label: "Very confident" },
  { value: "somewhat_confident",   label: "Somewhat confident" },
  { value: "need_more_information",label: "Need more information" },
  { value: "would_like_contact",   label: "I'd like someone to contact me" },
];

export default function ConsumerJourneyPortal() {
  const { token } = useParams();
  const [journey, setJourney] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [askText, setAskText] = useState("");
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [naviLoading, setNaviLoading] = useState(false);
  const [preConfidence, setPreConfidence] = useState(null);
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefTranslation, setBriefTranslation] = useState(null);
  const [briefTranslating, setBriefTranslating] = useState(false);
  const [goalDraft, setGoalDraft] = useState("");
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const { prefs } = useAccessibility();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/j/${token}`)
      .then(r => {
        setJourney(r.data);
        emitSignal("review.opened.v1", { product_type: r.data.product?.product_type, product_name: r.data.product?.product_name }, { journey_id: r.data.journey_id, tenant_id: null });
        // Fetch product-type-matched training videos from the NAVI corpus
        const ptype = r.data.product?.product_type;
        if (ptype) {
          setVideosLoading(true);
          axios.get(`${API}/corpus/training-videos?product_type=${encodeURIComponent(ptype)}`)
            .then(res => setVideos(res.data || []))
            .catch(() => setVideos([]))
            .finally(() => setVideosLoading(false));
        }
        // Register the token on-device so the wallet home lists this product review
        registerJourneyToken(token, {
          journey_id: r.data.journey_id,
          product_name: r.data.product?.product_name || r.data.product_of_interest,
          product_type: r.data.product?.product_type,
        });
        updateWallet({
          last_journey_url: window.location.href,
          last_journey_id: r.data.journey_id,
          last_journey_product: r.data.product?.product_name || r.data.product_of_interest,
        });
        // Auto-generate the rich Claude 4.5 brief (reads uploaded docs) if not cached
        if (!r.data.brief_generated_at || !r.data.product_overview) {
          setBriefLoading(true);
          axios.post(`${API}/j/${token}/brief`, {})
            .then(res => setJourney(res.data))
            .catch(() => {})
            .finally(() => setBriefLoading(false));
        }
        if (!r.data.navi_summary) {
          setNaviLoading(true);
          axios.post(`${API}/j/${token}/navi-summary`)
            .then(res => setJourney(prev => ({ ...(prev || {}), ...res.data })))
            .catch(() => {})
            .finally(() => setNaviLoading(false));
        }
      })
      .catch(e => setError(e?.response?.data?.detail || "This link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const call = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/j/${token}/${path}`, body || {});
      setJourney(data);
      emitSignal(`review.${path.replace("-", "_")}.recorded.v1`, { body }, { journey_id: data.journey_id });
      return data;
    } finally { setBusy(false); }
  };

  const translateNavi = async () => {
    if (!journey?.navi_summary || prefs.language === "en") return;
    setTranslating(true);
    try {
      const { data } = await axios.post(`${API}/wallet/translate`, {
        text: journey.navi_summary, target_language: prefs.language,
      });
      setTranslated(data.translated);
      emitSignal("navi.summary.translated.v1", { target_language: prefs.language }, { journey_id: journey.journey_id });
    } finally { setTranslating(false); }
  };

  const capturePreConfidence = (value) => {
    setPreConfidence(value);
    emitSignal("review.pre_confidence.captured.v1", { pre_confidence: value }, { journey_id: journey?.journey_id });
    updateWallet({ [`pre_conf_${journey.journey_id}`]: value });
  };

  const addGoal = async () => {
    const g = goalDraft.trim();
    if (!g) return;
    const next = [...(journey?.consumer_goals || []), g].slice(0, 8);
    setGoalDraft("");
    try {
      const { data } = await axios.post(`${API}/j/${token}/goals`, { goals: next });
      setJourney(data);
      // Regenerate the brief so goal_fit reflects the new goals
      setBriefLoading(true);
      axios.post(`${API}/j/${token}/brief`, { force: true })
        .then(res => setJourney(res.data))
        .catch(() => {})
        .finally(() => setBriefLoading(false));
    } catch { /* silent */ }
  };

  const removeGoal = async (idx) => {
    const next = (journey?.consumer_goals || []).filter((_, i) => i !== idx);
    try {
      const { data } = await axios.post(`${API}/j/${token}/goals`, { goals: next });
      setJourney(data);
      if (data.product_overview) {
        setBriefLoading(true);
        axios.post(`${API}/j/${token}/brief`, { force: true })
          .then(res => setJourney(res.data))
          .catch(() => {})
          .finally(() => setBriefLoading(false));
      }
    } catch { /* silent */ }
  };

  const translateBrief = async () => {
    if (!journey?.product_overview || prefs.language === "en") return;
    setBriefTranslating(true);
    try {
      const { data } = await axios.post(`${API}/j/${token}/brief/translate`, { target_language: prefs.language });
      setBriefTranslation(data);
      emitSignal("brief.translated.v1", { target_language: prefs.language }, { journey_id: journey.journey_id });
    } finally { setBriefTranslating(false); }
  };

  const uploadDocument = async (file, kind) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("kind", kind || "supporting");
    try {
      const { data } = await axios.post(`${API}/j/${token}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJourney(data);
      setBriefTranslation(null);
      // Auto-regenerate the brief so NAVI reads the new doc immediately
      setBriefLoading(true);
      axios.post(`${API}/j/${token}/brief`, { force: true })
        .then(r => setJourney(r.data))
        .catch(() => {})
        .finally(() => setBriefLoading(false));
      emitSignal("dev.document.attached.v1", { filename: file.name, size: file.size, kind }, { journey_id: journey.journey_id });
    } catch (e) {
      alert(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const removeDocument = async (docId) => {
    try {
      const { data } = await axios.delete(`${API}/j/${token}/documents/${docId}`);
      setJourney(data);
      setBriefTranslation(null);
      if (data.documents.length > 0) {
        setBriefLoading(true);
        axios.post(`${API}/j/${token}/brief`, { force: true })
          .then(r => setJourney(r.data))
          .catch(() => {})
          .finally(() => setBriefLoading(false));
      }
    } catch (e) {
      alert(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  if (loading) return <Shell><Skeleton /></Shell>;
  if (error || !journey) return <Shell><InvalidLink error={error} /></Shell>;

  const p = journey.product || {};
  const b = journey.banker || {};
  const status = journey.review_status || "reviewing";
  const assessmentUnlocked = status === "interested" || status === "decision_recorded";
  const assessmentDone = journey.assessment_score != null;
  const answered = journey.decision_confidence?.answer && journey.decision_confidence.answer !== "not_answered";
  const completed = journey.status === "completed";
  const sentAgo = timeAgo(journey.delivered_at || journey.triggered_at);

  return (
    <Shell>
      <div data-testid="portal-page" className="max-w-2xl mx-auto px-4 py-6 space-y-4">

        {/* ═══ HERO — Institution product review ═══ */}
        <div
          data-testid="wallet-hero"
          className="rounded-2xl overflow-hidden shadow-lg"
          style={{ background: `linear-gradient(160deg, ${NAVY} 0%, ${NAVY_DARK} 100%)` }}
        >
          <div className="px-6 pt-6 pb-5 text-white">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center font-display font-bold text-lg"
                style={{ background: GOLD, color: NAVY }}
              >
                {(b.advisor_name || "A").trim().charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>
                  Institution Product Review
                </div>
                <div className="text-sm font-semibold mt-0.5 truncate">
                  {b.advisor_name || "Your bank"}
                  {b.branch ? <span className="font-normal text-white/70"> · {b.branch}</span> : null}
                </div>
                <div className="text-[11px] text-white/60 truncate">Sent to {firstName(journey.consumer_name)} · {sentAgo}</div>
              </div>
            </div>
            <div className="h-px my-5" style={{ background: "rgba(255,255,255,0.12)" }} />
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>
              {(p.product_type || "Product").replace(/_/g, " ")}
            </div>
            <div className="font-display text-2xl sm:text-3xl font-semibold mt-1 leading-tight">
              {p.product_name}
            </div>
            {(p.monthly_premium != null || p.sum_assured != null) && (
              <div className="mt-3 text-sm text-white/80">
                {p.monthly_premium != null && <><strong className="text-white">{money(p.monthly_premium, p.currency)}</strong>/mo</>}
                {p.sum_assured != null && <> · <strong className="text-white">{money(p.sum_assured, p.currency)}</strong> cover</>}
                {p.term_months != null && <> · {p.term_months} months</>}
              </div>
            )}
          </div>
        </div>

        {/* ═══ PRE-CONFIDENCE — captured before consumer engages with docs ═══ */}
        {!preConfidence && !journey.acknowledged_at && (
          <WalletCard testId="wallet-pre-confidence">
            <SectionHead icon={Sparkles} accent>Before we start</SectionHead>
            <p className="text-xs text-slate-500 mt-1">How confident do you feel about this product right now, before reading anything?</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                { v: "not_confident", l: "Not confident" },
                { v: "somewhat",      l: "Somewhat"      },
                { v: "very_confident",l: "Very"          },
              ].map(c => (
                <button
                  key={c.v}
                  onClick={() => capturePreConfidence(c.v)}
                  data-testid={`pre-conf-${c.v}`}
                  className="px-3 py-3 rounded-xl border text-sm bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                >{c.l}</button>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Your bank sees your before-and-after so they can improve their explanations.</p>
          </WalletCard>
        )}

        {/* ═══ YOUR GOALS — comes first, drives goal-fit ═══ */}
        <WalletCard testId="wallet-goals">
          <SectionHead icon={ShieldCheck} accent>Your goals for this product</SectionHead>
          <p className="text-xs text-slate-500 mt-1">
            Tell NAVI what you want this product to do for you. We&apos;ll check how well the product&apos;s actual terms match.
          </p>
          <div className="flex gap-2 mt-3">
            <input
              type="text"
              value={goalDraft}
              onChange={e => setGoalDraft(e.target.value)}
              onKeyDown={e => e.key === "Enter" && addGoal()}
              placeholder={'e.g. "Cover my family for 20 years" or "Grow savings for retirement"'}
              disabled={busy || completed}
              data-testid="goal-input"
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
            />
            <button
              onClick={addGoal}
              disabled={busy || completed || !goalDraft.trim()}
              data-testid="goal-add-btn"
              className="px-4 py-2 rounded-xl text-white font-medium text-sm transition disabled:opacity-40"
              style={{ background: NAVY }}
            >
              Add
            </button>
          </div>
          {(journey.consumer_goals || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {(journey.consumer_goals || []).map((g, i) => (
                <span key={i} data-testid={`goal-chip-${i}`} className="inline-flex items-center gap-1.5 rounded-full text-xs px-3 py-1.5 border" style={{ background: `${GOLD}15`, borderColor: `${GOLD}55`, color: NAVY }}>
                  {g}
                  <button onClick={() => removeGoal(i)} data-testid={`goal-remove-${i}`} className="text-slate-500 hover:text-slate-800" aria-label="Remove goal">×</button>
                </span>
              ))}
            </div>
          )}
        </WalletCard>

        {/* ═══ RICH PRODUCT BRIEF — Claude 4.5 reads the docs ═══ */}
        <WalletCard testId="wallet-brief">
          <div className="flex items-center justify-between">
            <SectionHead icon={Sparkles} accent>What NAVI read in your documents</SectionHead>
            {journey.product_overview && prefs.language !== "en" && !briefTranslation && (
              <button
                onClick={translateBrief}
                disabled={briefTranslating}
                data-testid="brief-translate-btn"
                className="text-[11px] font-medium hover:underline disabled:opacity-40"
                style={{ color: NAVY }}
              >
                {briefTranslating ? "Translating…" : `Read in ${prefs.language.toUpperCase()}`}
              </button>
            )}
          </div>

          {briefLoading && !journey.product_overview ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> NAVI is reading the {journey.documents.length} document{journey.documents.length === 1 ? "" : "s"} the {b.advisor_name ? "advisor" : "bank"} attached…
            </div>
          ) : !journey.product_overview ? (
            <div className="mt-3 text-sm text-slate-500 italic">Brief not yet generated.</div>
          ) : (
            <div className="mt-3 space-y-5">
              <BriefSection title="Product overview" testid="brief-overview">
                <p className="text-[15px] leading-relaxed text-slate-800">
                  {briefTranslation?.product_overview || journey.product_overview}
                </p>
              </BriefSection>

              <BriefSection title="Key risks" testid="brief-risks" accent="rose">
                <BulletList items={briefTranslation?.key_risks || journey.key_risks} tone="rose" />
              </BriefSection>

              <BriefSection title="Things to consider before agreeing" testid="brief-consider" accent="amber">
                <BulletList items={briefTranslation?.things_to_consider || journey.things_to_consider} tone="amber" />
              </BriefSection>

              <BriefSection title="Common scams for this product" testid="brief-scams" accent="slate">
                <BulletList items={briefTranslation?.common_scams || journey.common_scams} tone="slate" icon={AlertTriangle} />
              </BriefSection>

              {journey.goal_fit && (
                <BriefSection title="Does this match your goals?" testid="brief-goal-fit">
                  <GoalFitBadge fit={journey.goal_fit} rationale={briefTranslation?.goal_fit_rationale} />
                </BriefSection>
              )}

              <div className="text-[10px] font-mono text-slate-400 pt-2 border-t border-slate-100">
                Read {journey.documents_read?.length || 0} of {journey.documents.length} document{journey.documents.length === 1 ? "" : "s"}. NAVI explains only — decisions are yours.
                {briefTranslation && <> · translated to {briefTranslation.language.toUpperCase()}</>}
              </div>
              <FeedbackRow
                targetKind="brief"
                targetId={journey.journey_id}
                corpusItemIds={journey.brief_corpus_ids || []}
                className="pt-1"
              />
            </div>
          )}
        </WalletCard>

        {/* ═══ PRODUCT TRAINING VIDEOS — matched to product_type ═══ */}
        {(videos.length > 0 || videosLoading) && (
          <WalletCard testId="wallet-training">
            <SectionHead icon={Sparkles} accent>Watch before you decide</SectionHead>
            <p className="text-xs text-slate-500 mt-1">
              Short training NAVI matched to <strong>{(journey.product?.product_type || "this product").replace(/_/g, " ")}</strong>. Optional but recommended.
            </p>
            {videosLoading && videos.length === 0 && (
              <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Finding relevant training…
              </div>
            )}
            <div className="mt-4 space-y-4" data-testid="training-video-list">
              {videos.map(v => (
                <div key={v.id} data-testid={`training-video-${v.id}`}>
                  <VideoEmbed url={v.video_url} title={v.title} duration_seconds={v.duration_seconds} />
                  <div className="mt-2 flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold" style={{ color: NAVY }}>{v.title}</div>
                      {v.notes && <div className="text-[11px] text-slate-500 mt-0.5">{v.notes}</div>}
                    </div>
                    {v.duration_seconds > 0 && (
                      <span className="text-[10px] font-mono text-slate-500 tabular-nums rounded bg-slate-100 px-1.5 py-0.5">{formatDuration(v.duration_seconds)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </WalletCard>
        )}

        {/* ═══ DEV UPLOAD PANEL — attach real docs for NAVI to learn from ═══ */}
        <DevUploadPanel
          documents={journey.documents}
          onUpload={uploadDocument}
          onRemove={removeDocument}
          briefLoading={briefLoading}
        />

        {/* ═══ STATUS SELECTOR ═══ */}
        <WalletCard testId="wallet-status">
          <SectionHead>Where are you in your review?</SectionHead>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {STATUS_OPTIONS.map(opt => {
              const active = status === opt;
              return (
                <button
                  key={opt}
                  onClick={() => call("status", { review_status: opt })}
                  disabled={busy || completed}
                  data-testid={`product-status-${opt}`}
                  className={`text-sm px-4 py-3 rounded-xl border transition ${
                    active
                      ? "text-white shadow-md"
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  } disabled:opacity-50`}
                  style={active ? { background: NAVY, borderColor: NAVY } : {}}
                >
                  {STATUS_LABEL[opt]}
                </button>
              );
            })}
          </div>
        </WalletCard>

        {/* ═══ NAVI plain-language summary ═══ */}
        <WalletCard testId="wallet-navi">
          <div className="flex items-center justify-between">
            <SectionHead icon={Sparkles} accent>NAVI · Plain-language summary</SectionHead>
            {journey.navi_summary && prefs.language !== "en" && !translated && (
              <button onClick={translateNavi} disabled={translating} data-testid="navi-translate" className="text-[11px] font-medium hover:underline disabled:opacity-40" style={{ color: NAVY }}>
                {translating ? "Translating…" : `Translate to ${prefs.language.toUpperCase()}`}
              </button>
            )}
          </div>
          {naviLoading && !journey.navi_summary ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> NAVI is reading the documents…
            </div>
          ) : journey.navi_summary ? (
            <div className="mt-3">
              <p className="text-[15px] leading-relaxed text-slate-800">{translated || journey.navi_summary}</p>
              <div className="mt-2 text-[10px] font-mono text-slate-400">
                NAVI does not give advice. It explains, so you can decide.
                {translated && <> · translated to {prefs.language.toUpperCase()}</>}
              </div>
            </div>
          ) : (
            <div className="mt-3 text-sm text-slate-500 italic">Summary unavailable.</div>
          )}
        </WalletCard>

        {/* ═══ DOCUMENTS VAULT ═══ */}
        <WalletCard testId="wallet-documents">
          <SectionHead icon={FileText}>Your document vault · {journey.documents.length}</SectionHead>
          {journey.documents.length === 0 ? (
            <div className="mt-3 text-sm text-slate-500 italic">No documents attached.</div>
          ) : (
            <div className="mt-3 divide-y divide-slate-100">
              {journey.documents.map(d => (
                <div key={d.document_id} data-testid={`doc-${d.kind}`} className="py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                       style={{ background: `${GOLD}22`, color: NAVY }}>
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: NAVY }}>
                      {DOC_KIND_LABEL[d.kind] || d.kind}
                    </div>
                    <div className="text-sm font-medium text-slate-800 truncate">{d.filename}</div>
                    {d.certificate_id && (
                      <a
                        href={`/verify/${d.certificate_id}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`doc-cert-${d.document_id}`}
                        className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium rounded px-1.5 py-0.5"
                        style={{ background: `${GOLD}30`, color: NAVY }}
                        title="Altara-Verified · click to open the Trust Certificate"
                      >
                        <ShieldCheck className="w-3 h-3" /> Altara-Verified · {d.certificate_id}
                      </a>
                    )}
                  </div>
                  {d.url ? (
                    <a href={d.url} target="_blank" rel="noreferrer" onClick={() => emitSignal("document.opened.v1", { kind: d.kind, filename: d.filename }, { journey_id: journey.journey_id })} className="text-xs font-medium inline-flex items-center gap-1 hover:underline" style={{ color: NAVY }}>
                      Open <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <span className="text-[10px] text-slate-400 italic">inline</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </WalletCard>

        {/* ═══ QUICK CHECK — unlocks when Interested ═══ */}
        <WalletCard testId="wallet-quick-check">
          <div className="flex items-center justify-between">
            <SectionHead icon={assessmentUnlocked ? CheckCircle2 : Lock}>Quick understanding check</SectionHead>
            {!assessmentUnlocked && (
              <span className="text-[10px] uppercase tracking-widest text-slate-400 font-medium">Unlocks at &ldquo;Interested&rdquo;</span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {assessmentUnlocked
              ? "A short check — helps your bank see where you feel confident and where you'd like more explanation."
              : "Change your status to 'Interested' above to unlock the check."}
          </p>

          {assessmentUnlocked && !assessmentDone && (
            <div className="mt-4 space-y-4">
              {QUICK_CHECKS.map((c, idx) => (
                <div key={c.id}>
                  <div className="text-sm text-slate-800 mb-2">
                    <span className="font-mono text-[11px] text-slate-400 mr-1.5">{idx + 1}.</span>{c.q}
                  </div>
                  <div className="flex gap-2">
                    {CHECK_OPTIONS.map(o => {
                      const active = assessmentAnswers[c.id] === o.value;
                      return (
                        <button
                          key={o.value}
                          onClick={() => setAssessmentAnswers(a => ({ ...a, [c.id]: o.value }))}
                          data-testid={`check-${c.id}-${o.value}`}
                          className={`flex-1 text-xs sm:text-sm px-3 py-2 rounded-xl border transition ${
                            active
                              ? "border-slate-800 bg-slate-800 text-white"
                              : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                          }`}
                        >
                          {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <button
                onClick={() => call("assessment", { answers: assessmentAnswers })}
                disabled={busy || Object.keys(assessmentAnswers).length < QUICK_CHECKS.length}
                data-testid="check-submit"
                className="w-full py-3 rounded-xl font-medium text-white transition disabled:opacity-40"
                style={{ background: NAVY }}
              >
                Submit understanding check
              </button>
            </div>
          )}

          {assessmentDone && (
            <div className="mt-4 rounded-xl p-4" style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}55` }}>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: NAVY }}>Understanding score</div>
              <div className="flex items-end gap-2 mt-1">
                <span className="font-display text-4xl font-semibold" style={{ color: NAVY }}>{journey.assessment_score}</span>
                <span className="text-sm text-slate-600 mb-1">/ 100</span>
              </div>
              <div className="mt-2 text-xs text-slate-700">
                {journey.assessment_score >= 80
                  ? "You have strong understanding of this product."
                  : journey.assessment_score >= 50
                  ? "You have a working understanding — a few areas would benefit from more explanation."
                  : "Your bank should walk through this in more detail before you decide."}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {Object.entries(journey.assessment_answers || {}).map(([k, v]) => (
                  <span key={k} className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-medium ${
                    v === "yes" ? "bg-emerald-100 text-emerald-800" :
                    v === "roughly" ? "bg-amber-100 text-amber-800" :
                                       "bg-rose-100 text-rose-800"
                  }`}>{k}: {v.replace("_", " ")}</span>
                ))}
              </div>
            </div>
          )}
        </WalletCard>

        {/* ═══ ASK NAVI ═══ */}
        <WalletCard testId="wallet-ask">
          <SectionHead icon={MessageCircleQuestion} accent>Ask NAVI a question</SectionHead>
          <p className="text-xs text-slate-500 mt-1">Every question is captured — it helps your bank explain better next time.</p>
          <div className="flex gap-2 mt-3">
            <input
              type="text"
              value={askText}
              onChange={e => setAskText(e.target.value)}
              placeholder={'e.g. "What happens if I cancel after 6 months?"'}
              disabled={busy || completed}
              data-testid="portal-ask-input"
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
            />
            <button
              onClick={async () => { if (askText.trim()) { await call("question", { text: askText.trim() }); setAskText(""); } }}
              disabled={busy || completed || !askText.trim()}
              data-testid="portal-ask-btn"
              className="px-4 py-2 rounded-xl text-white font-medium text-sm transition disabled:opacity-40"
              style={{ background: NAVY }}
            >
              Ask
            </button>
          </div>
          {(journey.questions || []).length > 0 && (
            <div className="mt-4 space-y-2">
              {journey.questions.map(q => (
                <div key={q.question_id} className="rounded-xl p-3 bg-slate-50 border border-slate-100">
                  <div className="text-sm text-slate-800">&ldquo;{q.text}&rdquo;</div>
                  {q.ai_categorised && q.inferred_gap && (
                    <div className="mt-1 text-[10px] font-mono uppercase tracking-widest inline-block px-2 py-0.5 rounded-full" style={{ background: `${GOLD}25`, color: NAVY }}>
                      NAVI heard: {q.inferred_gap}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </WalletCard>

        {/* ═══ DECISION CONFIDENCE ═══ */}
        <WalletCard testId="wallet-decision">
          <SectionHead icon={ShieldCheck}>How confident are you about this decision?</SectionHead>
          <p className="text-xs text-slate-500 mt-1">There is no rush and no wrong answer.</p>
          <div className="mt-3 grid gap-2">
            {DECISION_CHOICES.map(c => {
              const active = journey.decision_confidence?.answer === c.value;
              return (
                <button
                  key={c.value}
                  onClick={() => call("decision", { answer: c.value })}
                  disabled={busy || completed}
                  data-testid={`portal-decision-${c.value}`}
                  className={`text-left px-4 py-3 rounded-xl border text-sm transition ${
                    active ? "text-white shadow-md" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                  } disabled:opacity-40`}
                  style={active ? { background: NAVY, borderColor: NAVY } : {}}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
        </WalletCard>

        {/* ═══ CONFIRM & SEND ═══ */}
        <WalletCard testId="wallet-confirm">
          {completed ? (
            <div data-testid="portal-complete-done" className="text-center py-2">
              <CheckCircle2 className="w-8 h-8 mx-auto" style={{ color: GOLD }} />
              <div className="mt-2 font-display text-lg font-semibold" style={{ color: NAVY }}>Review complete</div>
              <div className="text-xs text-slate-600 mt-1">
                Your response has been sent to {b.advisor_name || "your bank"}.
              </div>
              <Link
                to={loadWallet() ? "/wallet" : "/wallet/onboarding"}
                data-testid="portal-open-wallet"
                className="mt-4 inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium text-white"
                style={{ background: NAVY }}
              >
                <Wallet className="w-3.5 h-3.5" />
                {loadWallet() ? "Open my Trust Wallet" : "Create my Trust Wallet"}
              </Link>
            </div>
          ) : (
            <>
              <SectionHead icon={Send}>Send my review to my bank</SectionHead>
              <p className="text-xs text-slate-500 mt-1 mb-3">
                By sending, you confirm you&rsquo;ve reviewed the documents above. This is not the same as accepting the product.
              </p>
              <button
                onClick={async () => {
                  await call("acknowledge", { accepted: true });
                  const done = await call("complete", {});
                  if (done) {
                    const w = loadWallet() || {};
                    const preConf = w[`pre_conf_${done.journey_id}`];
                    emitSignal("review.completed.v1", {
                      product_type: done.product?.product_type,
                      decision_confidence: done.decision_confidence?.answer,
                      pre_confidence: preConf,
                      assessment_score: done.assessment_score,
                      questions_asked: (done.questions || []).length,
                      confidence_delta: preConf && done.decision_confidence?.answer
                        ? computeConfidenceDelta(preConf, done.decision_confidence.answer)
                        : null,
                    }, { journey_id: done.journey_id });

                    const current = w.decisions || [];
                    updateWallet({
                      decisions: [{
                        journey_id: done.journey_id,
                        product_name: done.product?.product_name || done.product_of_interest,
                        institution: done.banker?.branch || done.banker?.advisor_name || "Your bank",
                        completed_at: done.completed_at || new Date().toISOString(),
                        confidence: done.decision_confidence?.answer,
                        pre_confidence: preConf,
                        score: done.assessment_score,
                      }, ...current],
                    });
                  }
                }}
                disabled={busy || !answered}
                data-testid="portal-complete-btn"
                className="w-full py-3 rounded-xl font-medium text-white transition disabled:opacity-40"
                style={{ background: NAVY }}
              >
                {answered ? "Send my review" : "Pick a confidence level above first"}
              </button>
            </>
          )}
          <div className="mt-4 pt-3 border-t border-slate-100 text-[10px] font-mono text-slate-400">
            Journey ID: {journey.journey_id} · Secure link · TLS
          </div>
        </WalletCard>

      </div>
    </Shell>
  );
}

// ─── Presentation helpers ────────────────────────────────────

function Shell({ children }) {
  return <WalletShell activeTab="journey">{children}</WalletShell>;
}

function WalletCard({ children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5"
    >
      {children}
    </section>
  );
}

function SectionHead({ icon: Icon, children, accent }) {
  return (
    <div className="flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4" style={{ color: accent ? GOLD : NAVY }} strokeWidth={2} />}
      <div className="font-semibold text-[15px]" style={{ color: NAVY }}>{children}</div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4 animate-pulse">
      <div className="rounded-2xl h-40" style={{ background: NAVY, opacity: 0.15 }} />
      <div className="rounded-2xl bg-white h-24" />
      <div className="rounded-2xl bg-white h-32" />
      <div className="rounded-2xl bg-white h-40" />
    </div>
  );
}

function InvalidLink({ error }) {
  return (
    <div data-testid="portal-invalid" className="max-w-md mx-auto text-center py-20 px-4">
      <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center" style={{ background: `${GOLD}22` }}>
        <AlertTriangle className="w-6 h-6" style={{ color: GOLD }} />
      </div>
      <div className="mt-4 font-display text-xl font-semibold" style={{ color: NAVY }}>Link unavailable</div>
      <div className="text-sm text-slate-600 mt-2">{error}</div>
      <div className="text-xs text-slate-400 mt-6">If you believe this is an error, please contact your advisor for a new link.</div>
    </div>
  );
}

function firstName(fullName) {
  if (!fullName) return "there";
  return String(fullName).trim().split(/\s+/)[0];
}

function computeConfidenceDelta(pre, post) {
  const M = { not_confident: 0, somewhat: 1, very_confident: 2,
              would_like_contact: 0, need_more_information: 0,
              somewhat_confident: 1 };
  const a = M[pre] ?? null;
  const b = M[post] ?? null;
  return a == null || b == null ? null : b - a;
}

function money(n, currency = "ZAR") {
  try {
    return new Intl.NumberFormat("en-ZA", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency} ${n}`;
  }
}

function timeAgo(iso) {
  if (!iso) return "just now";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, (Date.now() - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
}


function BriefSection({ title, testid, accent, children }) {
  const accentColor = accent === "rose" ? "#DC2626" : accent === "amber" ? "#D97706" : accent === "slate" ? "#475569" : NAVY;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: accentColor }} />
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: accentColor }}>{title}</div>
      </div>
      {children}
    </div>
  );
}

function BulletList({ items, tone, icon: Icon }) {
  const dot = tone === "rose" ? "bg-rose-500" : tone === "amber" ? "bg-amber-500" : "bg-slate-400";
  if (!items || items.length === 0) {
    return <div className="text-sm text-slate-500 italic">Nothing captured for this section.</div>;
  }
  return (
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5 text-[14px] leading-relaxed text-slate-800">
          {Icon ? (
            <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-1" style={{ color: "#DC2626" }} />
          ) : (
            <span className={`w-1.5 h-1.5 rounded-full ${dot} flex-shrink-0 mt-2`} />
          )}
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function GoalFitBadge({ fit, rationale }) {
  const alignment = fit?.alignment || "insufficient_info";
  const tone = {
    aligned:            { bg: "#10B98122", border: "#10B98166", label: "Well aligned with your goals",    color: "#047857" },
    partial:            { bg: "#F59E0B22", border: "#F59E0B66", label: "Partially matches your goals",    color: "#B45309" },
    misaligned:         { bg: "#EF444422", border: "#EF444466", label: "Does not match your goals well",  color: "#B91C1C" },
    insufficient_info:  { bg: "#94A3B822", border: "#94A3B866", label: "Not enough information to judge", color: "#475569" },
  }[alignment];
  return (
    <div className="rounded-xl p-4" style={{ background: tone.bg, border: `1px solid ${tone.border}` }}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: tone.color }}>{tone.label}</div>
      {(rationale || fit.rationale) && (
        <div className="mt-2 text-[14px] leading-relaxed text-slate-800">{rationale || fit.rationale}</div>
      )}
      {(fit.gaps || []).length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Gaps NAVI noticed</div>
          <ul className="space-y-1">
            {fit.gaps.map((g, i) => (
              <li key={i} className="text-[13px] text-slate-700 flex gap-2"><span className="text-slate-400">•</span>{g}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const UPLOAD_KINDS = [
  { value: "disclosure",     label: "Disclosure / TCF" },
  { value: "quote",          label: "Quote" },
  { value: "policy_wording", label: "Policy wording" },
  { value: "kyc",            label: "KYC / ID" },
  { value: "supporting",     label: "Supporting" },
];

function DevUploadPanel({ documents, onUpload, onRemove, briefLoading }) {
  const [kind, setKind] = useState("disclosure");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useMemo(() => ({ current: null }), []);

  const handleFiles = (files) => {
    if (!files || !files.length) return;
    Array.from(files).forEach(f => onUpload(f, kind));
  };

  return (
    <WalletCard testId="dev-upload">
      <div className="flex items-center justify-between">
        <SectionHead icon={Upload} accent>Attach real documents (dev / preview)</SectionHead>
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">preview mode</span>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Drop a real disclosure, quote or scam sample and NAVI will re-read everything. Files are stored inline on the journey and used only for this review.
      </p>

      <div className="mt-3 flex items-center gap-2">
        <label className="text-[11px] uppercase tracking-widest text-slate-500">Kind</label>
        <select
          value={kind}
          onChange={e => setKind(e.target.value)}
          data-testid="dev-upload-kind"
          className="text-sm rounded-lg border border-slate-200 px-2 py-1 focus:outline-none focus:border-slate-400"
        >
          {UPLOAD_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
        </select>
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => fileInputRef.current?.click()}
        data-testid="dev-upload-dropzone"
        className={`mt-3 rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition ${dragOver ? "border-slate-400 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}
      >
        <Upload className="w-6 h-6 mx-auto text-slate-400" />
        <div className="mt-2 text-sm text-slate-600">
          Drop file here or <span className="underline" style={{ color: NAVY }}>choose a file</span>
        </div>
        <div className="text-[11px] text-slate-400 mt-1">PDF, image or text · max 10 MB</div>
        <input
          ref={el => (fileInputRef.current = el)}
          type="file"
          data-testid="dev-upload-input"
          className="hidden"
          onChange={e => handleFiles(e.target.files)}
        />
      </div>

      {briefLoading && (
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> NAVI is re-reading everything…
        </div>
      )}

      {documents.length > 0 && (
        <div className="mt-4 space-y-1.5" data-testid="dev-upload-list">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">On this journey</div>
          {documents.map(d => (
            <div key={d.document_id} data-testid={`doc-row-${d.document_id}`} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-xs">
              <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="truncate text-slate-800">{d.filename}</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest">{d.kind}</div>
              </div>
              <button
                onClick={() => onRemove(d.document_id)}
                data-testid={`doc-remove-${d.document_id}`}
                className="text-slate-400 hover:text-rose-600 transition"
                aria-label="Remove document"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </WalletCard>
  );
}

